import React from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { SettingsCategory } from '../../config/SettingsCategory';
import { useSettingsPage } from '../../hooks/useSettingsPage';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { NativePlatform } from '../../Contracts/base';
import { DISPLAY_SETTING_IDS } from '../../config/SettingIds';
import { SettingsRenderer } from '../../SettingsProvider/SettingsRenderer';
import { MonitorLayout } from '../../customSettings/MonitorLayout/MonitorLayout';
import { getSettingConfig } from '../../config/SettingsDefinitions';

interface DisplaySettingsPageProps {
	title: string;
	description?: string;
}

export const DisplaySettingsPage: React.FC<DisplaySettingsPageProps> = ({ title }) => {
	const { settingsState, availableSettingIds, error, handleChangeBySettingId } =
		useSettingsPage({
			category: SettingsCategory.Display,
			defaultValue: 'match-device',
		});

	// Get platform from bridge
	const bridge = SettingsNativeBridgeFactory.getBridge();
	const platform = bridge.getPlatform();

	// Options filter for Mac platform
	const optionsFilter = (settingId: string, options: any[]) => {
		// For Mac platform, show only first two options for session resolution
		if (
			platform === NativePlatform.MAC &&
			settingId === DISPLAY_SETTING_IDS.SESSION_RESOLUTION
		) {
			return options.slice(0, 2);
		}
		return options;
	};

	// Use SettingsRenderer for all settings
	const renderSettingById = (settingId: string) => {
		// Custom rendering for MonitorLayout
		if (settingId === DISPLAY_SETTING_IDS.MONITOR_LAYOUT) {
			const config = getSettingConfig(settingId);
			return (
				<MonitorLayout
					key={settingId}
					title={config?.title}
					description={config?.description}
					learnMoreUrl={config?.learnMoreUrl}
					learnMoreText={config?.learnMoreText}
				/>
			);
		}

		return SettingsRenderer({
			settingId,
			settingsState,
			handleChange: handleChangeBySettingId,
			optionsFilter,
		});
	};

	return (
		<SettingsPage
			title={title}
			settings={
				<>
					{error ? (
						<div className="error-message" data-testid="error-message">
							{error}
						</div>
					) : (
						<>
							{/* Dynamically render all available settings */}
							{availableSettingIds.map((settingId) => renderSettingById(settingId))}
						</>
					)}
				</>
			}
		/>
	);
};
